import { useState, useEffect } from "react";
import { Form, Input, Button, Space, Table, Tag, Switch, Modal, message, Popconfirm, Spin, Card } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, KeyOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import {
  getWebhookClients,
  createWebhookClient,
  updateWebhookClient,
  deleteWebhookClient,
  toggleWebhookClient,
  type WebhookClient,
} from "../../api/api";

interface WebhookClientFormData {
  name: string;
  secret: string;
  enabled: boolean;
  rate_limit: number;
  allowed_ips: string; // Comma-separated string for form input
  allowed_sources: string; // Comma-separated string for form input
  description: string;
}


function ConfigurePage() {
  // Webhook client management state
  const [webhookClients, setWebhookClients] = useState<WebhookClient[]>([]);
  const [loading, setLoading] = useState(false);
  const [isWebhookModalVisible, setIsWebhookModalVisible] = useState(false);
  const [editingClient, setEditingClient] = useState<WebhookClient | null>(null);
  const [webhookForm] = Form.useForm();

  // Load webhook clients on component mount
  useEffect(() => {
    loadWebhookClients().catch((error) => {
      console.error('Failed to load webhook clients on mount:', error);
    });
  }, []);

  const loadWebhookClients = async (): Promise<void> => {
    setLoading(true);
    try {
      const data = await getWebhookClients();
      setWebhookClients(data.clients || []);
    } catch (error: any) {
      console.error('Error loading webhook clients:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load webhook clients';
      message.error(errorMessage);
      setWebhookClients([]);
    } finally {
      setLoading(false);
    }
  };


  // Webhook client management functions
  const handleAddWebhookClient = () => {
    setEditingClient(null);
    webhookForm.resetFields();
    setIsWebhookModalVisible(true);
  };

  const handleEditWebhookClient = (client: WebhookClient) => {
    setEditingClient(client);
    webhookForm.setFieldsValue({
      name: client.name,
      secret: client.secret === '***hidden***' ? '' : client.secret,
      enabled: client.enabled,
      rate_limit: client.rate_limit,
      allowed_ips: (client.allowed_ips || []).join(', '),
      allowed_sources: (client.allowed_sources || []).join(', '),
      description: client.description || ''
    });
    setIsWebhookModalVisible(true);
  };

  const handleDeleteWebhookClient = async (clientId: string): Promise<void> => {
    try {
      await deleteWebhookClient(clientId);
      setWebhookClients(prev => prev.filter(client => client.id !== clientId));
      message.success('Webhook client deleted successfully');
    } catch (error: any) {
      console.error('Error deleting webhook client:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete webhook client';
      message.error(errorMessage);
    }
  };

  const handleWebhookSubmit = async (values: WebhookClientFormData): Promise<void> => {
    try {
      const clientData = {
        name: values.name,
        secret: values.secret,
        enabled: values.enabled,
        rate_limit: values.rate_limit,
        allowed_ips: values.allowed_ips.split(',').map(ip => ip.trim()).filter(ip => ip),
        allowed_sources: values.allowed_sources.split(',').map(source => source.trim()).filter(source => source),
        description: values.description
      };

      if (editingClient) {
        // Update existing client
        const updatedClient = await updateWebhookClient(editingClient.id, clientData);
        setWebhookClients(prev => prev.map(client => 
          client.id === editingClient.id ? updatedClient : client
        ));
        message.success('Webhook client updated successfully');
      } else {
        // Add new client
        const newClient = await createWebhookClient(clientData);
        setWebhookClients(prev => [...prev, newClient]);
        message.success('Webhook client created successfully');
      }

      setIsWebhookModalVisible(false);
      setEditingClient(null);
      webhookForm.resetFields();
    } catch (error) {
      console.error('Error saving webhook client:', error);
      message.error('Failed to save webhook client');
    }
  };

  const handleToggleWebhookClient = async (clientId: string, enabled: boolean): Promise<void> => {
    try {
      await toggleWebhookClient(clientId, enabled);
      setWebhookClients(prev => prev.map(client => 
        client.id === clientId ? { ...client, enabled } : client
      ));
      message.success(`Webhook client ${enabled ? 'enabled' : 'disabled'} successfully`);
    } catch (error: any) {
      console.error('Error toggling webhook client:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to toggle webhook client';
      message.error(errorMessage);
    }
  };

  // Webhook client table columns
  const webhookColumns: ColumnsType<WebhookClient> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: WebhookClient) => (
        <Space>
          <KeyOutlined />
          <span>{text}</span>
          {record.description && (
            <span style={{ color: '#666', fontSize: '12px' }}>({record.description})</span>
          )}
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'enabled',
      key: 'enabled',
      render: (enabled: boolean, record: WebhookClient) => (
        <Switch
          checked={enabled}
          onChange={(checked: boolean) => handleToggleWebhookClient(record.id, checked)}
          checkedChildren="Enabled"
          unCheckedChildren="Disabled"
        />
      ),
    },
    {
      title: 'Rate Limit',
      dataIndex: 'rate_limit',
      key: 'rate_limit',
      render: (rate_limit: number) => `${rate_limit} req/min`,
    },
    {
      title: 'Allowed IPs',
      dataIndex: 'allowed_ips',
      key: 'allowed_ips',
      render: (ips: string[] | undefined) => (
        <Space wrap>
          {(ips || []).map((ip, index) => (
            <Tag key={index} color="blue">{ip}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Allowed Sources',
      dataIndex: 'allowed_sources',
      key: 'allowed_sources',
      render: (sources: string[] | undefined) => (
        <Space wrap>
          {(sources || []).map((source, index) => (
            <Tag key={index} color="green">{source}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: WebhookClient) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEditWebhookClient(record)}
          />
          <Popconfirm
            title="Are you sure you want to delete this webhook client?"
            onConfirm={() => handleDeleteWebhookClient(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading && webhookClients.length === 0) {
    return (
      <div className="admin-content-container">
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className="admin-content-container">
      <div className="page-header-row">
        <h2 className="page-title">Webhook Configuration</h2>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddWebhookClient}
        >
          Add Webhook Client
        </Button>
      </div>

      <Card>
        <Table
          columns={webhookColumns}
          dataSource={webhookClients}
          rowKey="id"
          loading={loading}
          pagination={false}
          size="small"
        />
      </Card>


      {/* Webhook Client Modal */}
      <Modal
        title={editingClient ? 'Edit Webhook Client' : 'Add Webhook Client'}
        open={isWebhookModalVisible}
        onCancel={() => {
          setIsWebhookModalVisible(false);
          webhookForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={webhookForm}
          layout="vertical"
          onFinish={handleWebhookSubmit}
        >
          <Form.Item
            name="name"
            label="Client Name"
            rules={[{ required: true, message: 'Please enter client name' }]}
          >
            <Input placeholder="e.g., citizens-info, udaras" />
          </Form.Item>

          <Form.Item
            name="secret"
            label="Webhook Secret"
            rules={[{ required: true, message: 'Please enter webhook secret' }]}
          >
            <Input.Password placeholder="Enter webhook secret key" />
          </Form.Item>

          <Form.Item
            name="enabled"
            label="Status"
            valuePropName="checked"
          >
            <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
          </Form.Item>

          <Form.Item
            name="rate_limit"
            label="Rate Limit (requests per minute)"
            rules={[{ required: true, message: 'Please enter rate limit' }]}
          >
            <Input type="number" placeholder="100" />
          </Form.Item>

          <Form.Item
            name="allowed_ips"
            label="Allowed IP Addresses"
            help="Comma-separated list of IP addresses or CIDR ranges (e.g., 192.168.1.0/24, 10.0.0.1)"
          >
            <Input.TextArea
              rows={3}
              placeholder="192.168.1.0/24, 10.0.0.1, 203.0.113.0/24"
            />
          </Form.Item>

          <Form.Item
            name="allowed_sources"
            label="Allowed Sources"
            help="Comma-separated list of allowed source systems (e.g., cms, docs, api-gateway)"
          >
            <Input.TextArea
              rows={3}
              placeholder="cms, docs, api-gateway, workflow"
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <Input.TextArea
              rows={2}
              placeholder="Optional description for this webhook client"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setIsWebhookModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingClient ? 'Update' : 'Create'} Client
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default ConfigurePage;
